import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { useCallback, useState } from 'react';

import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { z } from 'zod';
import { InvestorGetData } from '~/api/def/endpoints/investors';
import { DocumentPDFField } from '~/lib/pdfViewer/types';
import styles from './EditFormFields.module.css';

export const validationSchema = z.object({
  capTableRowId: z.string().nonempty('Cap Table Row ID is required'),
  shareholder: z.string().nonempty('Shareholder is required'),
  shareholderId: z.string().nonempty('Shareholder ID is required'),
  shareClass: z.string().nonempty('Share Class is required'),
  shareClassId: z.string().nonempty('Share Class ID is required'),
  shareCount: z.number().min(1, 'Share count must be greater than 0'),
  shareCountId: z.string().nonempty('Share Count ID is required'),
  investmentCurrency: z.string().nonempty('Investment Currency is required'),
  investmentCurrencyId: z
    .string()
    .nonempty('Investment Currency ID is required'),
  investmentAmount: z.number().positive('Investment amount must be positive'),
  investmentAmountId: z.string().nonempty('Investment Amount ID is required'),
  pricePerShare: z.number().min(0, 'Price per share must be positive'),
  pricePerShareId: z.string().nonempty('Price Per Share ID is required'),
  investmentDate: z.date(),
  investmentDateId: z.string().nonempty('Investment Date ID is required'),
});

export const shareClassOptions = [
  { label: 'Ordinary', value: 'ordinary' },
  { label: 'A Ordinary', value: 'a_ordinary' },
  { label: 'B Ordinary', value: 'b_ordinary' },
  { label: 'C Ordinary', value: 'c_ordinary' },
  { label: 'Preferred', value: 'preferred' },
  { label: 'Seed Preferred', value: 'seed_preferred' },
  { label: 'Series A Preferred', value: 'series_a_preferred' },
  { label: 'Series B Preferred', value: 'series_b_preferred' },
  { label: 'Series C Preferred', value: 'series_c_preferred' },
  { label: 'Deferred', value: 'deferred' },
  { label: 'Growth', value: 'growth' },
  { label: 'Management', value: 'management' },
  { label: 'Founder', value: 'founder' },
];

export const shareClassValueToLabel = Object.fromEntries(
  shareClassOptions.map(({ value, label }) => [value, label]),
);

export type ValidationSchema = z.infer<typeof validationSchema>;

export type DocumentOverviewCapTableProps = {
  documentId: string;
  portfolioCompanyId: string;
  scrollToHighlight: (fieldId: string) => void;
  setPdfFields: (fields: DocumentPDFField[]) => void;
};

export const EditFormFields = ({
  control,
  errors,
  investors,
  onSubmit,
}: {
  control: Control<ValidationSchema> & {
    setValue: UseFormSetValue<ValidationSchema>;
  };
  errors: FieldErrors<ValidationSchema>;
  investors: InvestorGetData[];
  onSubmit: (data: ValidationSchema) => void;
}) => {
  console.log('EditFormFields investors:', investors);
  const [flashFields, setFlashFields] = useState<
    Set<'price' | 'quantity' | 'value'>
  >(new Set());

  const handleFlash = useCallback(
    (fields: ('price' | 'quantity' | 'value')[]) => {
      setFlashFields(new Set(fields));
      setTimeout(() => setFlashFields(new Set()), 500);
    },
    [],
  );

  const handleFieldChange = useCallback(
    (field: 'price' | 'quantity' | 'value', value: number | null) => {
      if (value === null) return;

      const price =
        field === 'price' ? value : control._formValues.pricePerShare;
      const quantity =
        field === 'quantity' ? value : control._formValues.shareCount;
      const investmentAmount =
        field === 'value' ? value : control._formValues.investmentAmount;

      if (field === 'price' || field === 'quantity') {
        if (price && quantity) {
          control.setValue('investmentAmount', price * quantity);
          handleFlash([field, 'value']);
        }
      } else if (field === 'value' && quantity) {
        control.setValue('pricePerShare', investmentAmount / quantity);
        handleFlash([field, 'price']);
      }
    },
    [control._formValues, control.setValue, handleFlash],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(control._formValues as ValidationSchema);
  };

  return (
    <form id="editCapTableForm" onSubmit={handleSubmit} className="grid">
      <div className="col-12 flex flex-column gap-1">
        <label htmlFor="shareholder">Shareholder</label>
        <Controller
          name="shareholder"
          control={control}
          render={({ field }) => (
            <Dropdown
              id="shareholder"
              value={field.value}
              options={investors.map((investor) => ({
                label: investor.name,
                value: investor.name,
                id: investor.id,
              }))}
              onChange={(e) => {
                field.onChange(e.value);
                const selectedInvestor = investors.find(
                  (investor) => investor.name === e.value,
                );
                // If it's a known investor, set their ID, otherwise clear it
                if (selectedInvestor) {
                  control.setValue('shareholderId', selectedInvestor.id);
                } else {
                  control.setValue('shareholderId', '');
                }
              }}
              filter
              editable
              placeholder="Select or enter a shareholder"
              className="w-full"
              style={{ maxWidth: '50rem' }}
            />
          )}
        />
        {errors.shareholder && (
          <small className="p-error">{errors.shareholder.message}</small>
        )}
      </div>
      <div className="col-6 flex flex-column gap-1">
        <label htmlFor="shareClass">Share Class</label>
        <Controller
          name="shareClass"
          control={control}
          render={({ field }) => (
            <Dropdown
              id="shareClass"
              options={shareClassOptions}
              {...field}
              className="w-full"
            />
          )}
        />
        {errors.shareClass && (
          <small className="p-error">{errors.shareClass.message}</small>
        )}
      </div>
      <div className="col-6 flex flex-column gap-1">
        <label htmlFor="shareCount">Share count</label>
        <Controller
          name="shareCount"
          control={control}
          render={({ field }) => {
            return (
              <InputNumber
                id="shareCount"
                locale="en-GB"
                {...field}
                min={1}
                onChange={(e) => {
                  field.onChange(e.value);
                  handleFieldChange('quantity', e.value);
                }}
                className={`w-full ${flashFields.has('quantity') ? styles.flashAnimation : ''}`}
              />
            );
          }}
        />
        {errors.shareCount && (
          <small className="p-error">{errors.shareCount.message}</small>
        )}
      </div>
      <div className="col-6 flex flex-column gap-1">
        <label htmlFor="investmentAmount">Investment Amount</label>
        <Controller
          name="investmentAmount"
          control={control}
          render={({ field }) => (
            <InputNumber
              id="investmentAmount"
              locale="en-GB"
              {...field}
              onChange={(e) => {
                field.onChange(e.value);
                handleFieldChange('value', e.value);
              }}
              className={`w-full ${flashFields.has('value') ? styles.flashAnimation : ''}`}
            />
          )}
        />
        {errors.investmentAmount && (
          <small className="p-error">{errors.investmentAmount.message}</small>
        )}
      </div>
      <div className="col-6 flex flex-column gap-1">
        <label htmlFor="pricePerShare">Price Per Share</label>
        <Controller
          name="pricePerShare"
          control={control}
          render={({ field }) => (
            <InputNumber
              locale="en-GB"
              id="pricePerShare"
              {...field}
              onChange={(e) => {
                field.onChange(e.value);
                handleFieldChange('price', e.value);
              }}
              minFractionDigits={2}
              maxFractionDigits={6}
              mode="decimal"
              className={`w-full ${flashFields.has('price') ? styles.flashAnimation : ''}`}
            />
          )}
        />
        {errors.pricePerShare && (
          <small className="p-error">{errors.pricePerShare.message}</small>
        )}
      </div>
      <div className="col-6 flex flex-column gap-1">
        <label htmlFor="investmentDate">Investment Date</label>
        <Controller
          name="investmentDate"
          control={control}
          render={({ field }) => {
            return (
              <Calendar
                id="investmentDate"
                {...field}
                onChange={(e) => {
                  if (e.value) {
                    field.onChange(e.value);
                  }
                }}
                dateFormat="dd/mm/yy"
                appendTo={document.body}
                className="w-full"
              />
            );
          }}
        />
        {errors.investmentDate && (
          <small className="p-error">{errors.investmentDate.message}</small>
        )}
      </div>
    </form>
  );
};
